import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { canView, getUserType } from "../permissions";
import API_BASE from "../config";

const Properties = () => {
    const navigate = useNavigate();
    const userType = getUserType();
    const allowView = canView(userType, "properties");

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        if (!allowView) {
            navigate('/dashboard');
            return;
        }
        fetch(`${API_BASE}/api/allrows`, {
            method: "POST",
            headers: { Authorization: "1", "Content-Type": "application/json" },
            body: JSON.stringify({ token: "3232", table: "projects" }),
        })
            .then((res) => res.json())
            .then((json) => {
                setLoading(false);
                if (Array.isArray(json.data)) setData(json.data);
                else setError("Failed to load properties.");
            })
            .catch((err) => {
                setLoading(false);
                setError("Error fetching projects.");
                console.error(err);
            });
    }, [allowView, navigate]);

    if (!allowView) return null;

    const filtered = data.filter((item) =>
        Object.values(item).join(" ").toLowerCase().includes(search.toLowerCase())
    );

    return (
        <>
            <style>{`
        .prop-page { display: flex; flex-direction: column; gap: 24px; }
        .prop-header { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px; }
        .prop-title { font-size: 24px; font-weight: 700; color: #0f172a; }
        .prop-bg { background: #fff; padding: 24px; border-radius: 16px; border: 1px solid #e2e8f0; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
        .prop-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 20px; }
        .prop-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px; transition: all 0.2s; position: relative; overflow: hidden; }
        .prop-card:hover { transform: translateY(-2px); box-shadow: 0 10px 30px rgba(0,0,0,0.06); }
        .prop-img-ph { height: 140px; background: #f1f5f9; border-radius: 8px; margin-bottom: 16px; display: flex; align-items: center; justify-content: center; color: #94a3b8; font-size: 40px; }
        
        .prop-name { font-size: 18px; font-weight: 700; color: #0f172a; margin-bottom: 6px; }
        .prop-loc { font-size: 13px; color: #64748b; font-weight: 500; display: flex; align-items: center; gap: 6px; margin-bottom: 14px; }
        .prop-desc { font-size: 13px; color: #64748b; line-height: 1.5; margin-bottom: 16px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        
        .prop-meta { display: flex; gap: 12px; margin-bottom: 16px; padding: 12px; background: #f8fafc; border-radius: 8px; }
        .prop-stat { flex: 1; text-align: center; }
        .prop-stat-val { font-size: 16px; font-weight: 700; color: #0f172a; }
        .prop-stat-lbl { font-size: 11px; color: #64748b; text-transform: uppercase; margin-top: 2px; }

        .prop-actions { display: flex; gap: 10px; }
        .prop-btn { flex: 1; padding: 10px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; border: 1px solid #e2e8f0; background: #fff; color: #334155; transition: all 0.2s; }
        .prop-btn:hover { background: #f1f5f9; color: #0f172a; border-color: #cbd5e1; }
        .prop-btn.primary { background: #3b82f6; color: #fff; border: none; }
        .prop-btn.primary:hover { background: #2563eb; }

        .prop-search { padding: 10px 16px; border-radius: 10px; border: 1px solid #e2e8f0; width: 100%; max-width: 300px; outline: none; }
        `}</style>

            <div className="prop-page">
                <div className="prop-header">
                    <div>
                        <h1 className="prop-title">Projects & Properties</h1>
                        <div style={{ fontSize: "14px", color: "#64748b", marginTop: "4px" }}>Manage your real estate projects</div>
                    </div>
                    <input
                        className="prop-search"
                        placeholder="Search projects..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                {loading ? (
                    <div className="prop-bg" style={{ textAlign: "center", padding: "60px" }}>Loading projects...</div>
                ) : filtered.length === 0 ? (
                    <div className="prop-bg" style={{ textAlign: "center", padding: "60px" }}>No projects found.</div>
                ) : (
                    <div className="prop-grid">
                        {filtered.map((item, index) => (
                            <div key={index} className="prop-card">
                                <div className="prop-img-ph">Build</div>
                                <div className="prop-name">{item.name || "Untitled Project"}</div>
                                <div className="prop-loc">📍 {item.location || item.city || "Unknown Location"}</div>
                                <div className="prop-desc">{item.description || "No description available for this project."}</div>

                                <div className="prop-meta">
                                    <div className="prop-stat">
                                        <div className="prop-stat-val">{item.units || "-"}</div>
                                        <div className="prop-stat-lbl">Total Units</div>
                                    </div>
                                    <div className="prop-stat">
                                        <div className="prop-stat-val">{item.shortform || "-"}</div>
                                        <div className="prop-stat-lbl">Short Form</div>
                                    </div>
                                </div>

                                <div className="prop-actions">
                                    <button
                                        className="prop-btn primary"
                                        onClick={() => navigate("/dashboard/properties/edit", { state: { property: item } })}
                                    >
                                        Edit Details
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
};

export default Properties;
